import { Box, Typography } from '@mui/material';
import React from 'react';
import SdCardVehicleApis from './SdCardVehicleApis';
import ApiEndpointDisplay from './ApiEndpointDisplay';
import ApiParametersTable from './ApiParametersTable';
import ApiResponseExamples from './ApiResponseExamples';
import ApiRequestExamples from './ApiRequestExamples';

const VehicleApis: React.FC = () => (
  <Box className="flex flex-col gap-4">
    <Box id="liveStream" className="flex flex-col border-b border-gray-200">
      <Typography variant="h6" fontWeight={600} fontSize={16} mb={1}>
        LIVE STREAM
      </Typography>
      <Typography variant="body2" fontSize={14} fontWeight={400} mb={2}>
        To obtain a live stream URL for a specific vehicle, you need to send a POST request and this request requires an Authorization token and the vehicleId for the vehicle you want to stream.
      </Typography>
      
      <Typography variant="body2" fontSize={14} fontWeight={600} mb={1}>
        Request
      </Typography>
      <ApiEndpointDisplay 
        method="POST" 
        url="https://backend.novusomnifleet.com/api/client/v1/live-stream" 
      />

      <ApiParametersTable 
        parameters={[
          { type: 'HEAD', name: 'Authorization', dataType: 'string', required: true },
          { type: 'POST', name: 'vehicleId', dataType: 'string', required: true },
          { type: 'POST', name: 'blurEnable', dataType: 'boolean', required: false }
        ]}
      />


      <Typography variant="body2" fontSize={14} fontWeight={400} mb={2}>
        In request body, send the vehicleId for the vehicle you want to stream.
      </Typography>

      <ApiRequestExamples
                examples={[
                    {
                    type: "headers",
                    headers: [
                        {
                        key: "Authorization",
                        value:
                            "eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJJQ1NBIiwidXNlcklkIjoiNjdkOTBkMDdlNGNhZTU3MDEwMzQyYzA4Iiwicm9sZXMiOiJST0xFX0ZMRUVUX01BTkFHRVIiLCJpYXQiOjE3NTQzNzIyNjksImV4cCI6MTc1NDQwODI2OX0.NItAkAwXNGKxoAsUTZSwB0lzFPHgW3I4ZwpLEY4c-Lo"
                        }
                    ],
                    label: "Request Headers"
                    },
                    {
                    type: "body",
                    contentType: "json",
                    value: `{
 "vehicleId": "Test346430117612",
 "blurEnable": true
}`,
                    label: "Request Body (with Blur)"
                    },
                    {
                    type: "body",
                    contentType: "json",
                    value: `{
 "vehicleId": "Test346430117612",
 "blurEnable": false
}`,
                    label: "Request Body (without Blur)"
                    }
                ]}
            />

      <ApiResponseExamples 
        examples={[
          {
            status: '200',
            description: 'Success (without Blur)',
            response: `{
                        "data": [
                          {
                            "cameraId": "01",
                            "cameraName": "ADAS",
                            "playBackUrl": "https://b-feb73142.kinesisvideo.ap-south-1.amazonaws.com/hls/v1/getHLSMasterPlaylist.m3u8?SessionToken=CiDj-04r2vksnInf3AjQJOA8kpznv9EqYdzj-QG8LUI6gxIQ9f-ajsRPUY1PGn-ITdwAbRoZX3zVx_CFr3GKjEwSNTt5GvU1zqPctQOr0CIgzQCiyIbnyYHh_k0mWs61k5qPICzP3Ws7r_Io2m_XLno~"
                          },
                          {
                            "cameraId": "04",
                            "cameraName": "DMS",
                            "playBackUrl": "https://b-feb73142.kinesisvideo.ap-south-1.amazonaws.com/hls/v1/getHLSMasterPlaylist.m3u8?SessionToken=CiD4XVFrV-ZWxyt1JdBpge4k2ukjGfxJuwctBgWxcDbHthIQ2kFnV3GE4wL9l5KRrKHqcBoZkeJuyhPHe7btLrt3sjKhCezBIQLZ8DF7OCIgR3mMb9Uq3Yya7xQjxsblnKTBFKf-HOZFmg_wTpQEdW4~"
                          }
                        ],
                        "message": "Live Url fetched Successfully",
                        "status": 200
                      }`
          },
          {
            status: '200',
            description: 'Success (With Blur)',
            response: `{
                        "data": [
                          "https://new.novusomnifleet.com/liveVideoExtModal/yourVehicleId?token=eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJJQ1NBIiwidXNlcklkIjoiNjdkOTBkMDdlNGNhZTU3MDEwMzQyYzA4Iiwicm9sZXMiOiJST0xFX0ZMRUVUX01BTkFHRVIiLCJpYXQiOjE3NTQzNzIyNjksImV4cCI6MTc1NDQwODI2OX0.NItAkAwXNGKxoAsUTZSwB0lzFPHgW3I4ZwpLEY4c-Lo"
                        ],
                        "message": "Live Url fetched Successfully",
                        "status": 200
                      }`
          },
          {
            status: '400',
            description: 'Bad Request - Missing vehicleId',
            response: `{
                        "success": false,
                        "message": "Invalid request body",
                        "errors": [
                          {
                            "code": "missing_field",
                            "field": "vehicleId",
                            "detail": "VehicleId is required"
                          }
                        ]
                      }`
          },
          {
            status: '401',
            description: 'Unauthorized - Invalid or missing token',
            response: `{
                          "success": false,
                          "message": "Invalid or missing token"
                        }`
          }
        ]}
      />
    </Box>
    <Box id="twoWayCommunication" className="flex flex-col border-b border-gray-200">
      <Typography variant="h6" fontWeight={600} fontSize={16} mb={1}>
        TWO WAY COMMUNICATION
      </Typography>
      <Typography variant="body2" fontSize={14} fontWeight={400} mb={2}>
        To obtain a two-way communication URL for a specific vehicle, you must send a POST request. This request requires an Authorization token in the headers and the vehicleId of the vehicle you want to communicate with in the request body.
      </Typography>
      
      <Typography variant="body2" fontSize={14} fontWeight={600} mb={1}>
        Request
      </Typography>
      <ApiEndpointDisplay 
        method="POST" 
        url="https://backend.novusomnifleet.com/api/client/v1/call" 
      />

      <ApiParametersTable 
        parameters={[
          { type: 'HEAD', name: 'Authorization', dataType: 'string', required: true },
          { type: 'POST', name: 'vehicleId', dataType: 'string', required: true }
        ]}
      />

       <Typography variant="body2" fontSize={14} fontWeight={400} mb={2}>
        In request body, send the vehicleId of the vehicle you want to communicate with.
      </Typography>

      <ApiRequestExamples
                examples={[
                    {
                    type: "headers",
                    headers: [
                        {
                        key: "Authorization",
                        value:
                            "eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJJQ1NBIiwidXNlcklkIjoiNjdkOTBkMDdlNGNhZTU3MDEwMzQyYzA4Iiwicm9sZXMiOiJST0xFX0ZMRUVUX01BTkFHRVIiLCJpYXQiOjE3NTQzNzIyNjksImV4cCI6MTc1NDQwODI2OX0.NItAkAwXNGKxoAsUTZSwB0lzFPHgW3I4ZwpLEY4c-Lo"
                        }
                    ],
                    label: "Request Headers"
                    },
                    {
                    type: "body",
                    contentType: "json",
                    value: `{
 "vehicleId": "1993"
}`,
                    label: "Request Body"
                    }
                ]}
            />

      <ApiResponseExamples 
        examples={[
          {
            status: '200',
            description: 'Success - Call URL generated',
            response: `{
                        "data": {
                          "callUrl": "https://new.novusomnifleet.com/sosModal/abc?token=eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ1dGFkbWluX2ZsZWV0bWFuYWdlciIsInJvbGVzIjoi9GTEVFVF9NQU5BR0VSIiwiaWF0IjoxNzQ0MTExNzIxLCJleHAiOjE3NDQxNDc3MjF9.YJR7T2IRvose-YAzXgH1LbjRKxBrus2_bl5-ffWLUu0"
                        },
                        "message": "Call Url fetched Successfully",
                        "status": 200
                      }`
          },
          {
            status: '400',
            description: 'Bad Request - Missing vehicleId',
            response: `{
                        "success": false,
                        "message": "Invalid request body",
                        "errors": [
                          {
                            "code": "missing_field",
                            "field": "vehicleId",
                            "detail": "VehicleId is required"
                          }
                        ]
                      }`
          },
          {
            status: '401',
            description: 'Unauthorized - Invalid credentials',
            response: `{
                          "success": false,
                          "message": "Invalid username or password"
                        }`
          }
        ]}
      />
    </Box>

    <Box id="getAlertVideo" className="flex flex-col border-b border-gray-200">
      <Typography variant="h6" fontWeight={600} fontSize={16} mb={1}>
        GET ALERT VIDEO ON DEMAND
      </Typography>
      <Typography variant="body2" fontSize={14} fontWeight={400} mb={2}>
        To get an alert video URL for a specific vehicle, you must send a POST request. This request requires an Authorization token in the headers and a request body containing the details of the vehicle for which you want to retrieve the alert video URL.
      </Typography>
      
      <Typography variant="body2" fontSize={14} fontWeight={600} mb={1}>
        Request
      </Typography>
      <ApiEndpointDisplay 
        method="POST" 
        url="https://backend.novusomnifleet.com/api/client/v1/get-alert-video-on-demand" 
      />

      <ApiParametersTable 
        parameters={[
          { type: 'HEAD', name: 'Authorization', dataType: 'string', required: true },
          { type: 'POST', name: 'vehicleId', dataType: 'string', required: true },
          { type: 'POST', name: 'timestamp', dataType: 'string', required: true },
          { type: 'POST', name: 'alertType', dataType: 'string', required: true }
        ]}
      />

      <Typography variant="body2" fontSize={14} fontWeight={400} mb={2}>
        In the request body, send the vehicleId, timestamp (you should retrieve this timestamp directly from the alert packet data you receive), alertType for which you want to retrieve the alert video URL.
      </Typography>

      <ApiRequestExamples
                examples={[
                    {
                    type: "headers",
                    headers: [
                        {
                        key: "Authorization",
                        value:
                            "eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJJQ1NBIiwidXNlcklkIjoiNjdkOTBkMDdlNGNhZTU3MDEwMzQyYzA4Iiwicm9sZXMiOiJST0xFX0ZMRUVUX01BTkFHRVIiLCJpYXQiOjE3NTQzNzIyNjksImV4cCI6MTc1NDQwODI2OX0.NItAkAwXNGKxoAsUTZSwB0lzFPHgW3I4ZwpLEY4c-Lo"
                        }
                    ],
                    label: "Request Headers"
                    },
                    {
                    type: "body",
                    contentType: "json",
                    value: `{
 "vehicleId": "1993",
 "timestamp": "2025-06-05:13:04:48",
 "alertType": "Yawning"
}`,
                    label: "Request Body"
                    }
                ]}
            />


      <ApiResponseExamples 
        examples={[
          {
            status: '200',
            description: 'Success - Video URLs retrieved',
            response: `{
                        "data": [
                          "https://vp2-vendor-data.s3.ap-south1.amazonaws.com/warning/863386074033605/2025-05-02/02_01_8_863386074033605_250502_155035.mp4?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-AmzDate=20250502T111326Z&X-Amz-SignedHeaders=host&X-Amz-Expires=604799&X-AmzCredential=AKIASZJLFSVFKHBGF7PE%2F20250502%2Fap-south-1%2Fs3%2Faws4_request&X-AmzSignature=971596fa47174589e9e7fd41622d3b340657ef7104b878ac84f0e5cd5604485f",
                          "https://vp2-vendor-data.s3.ap-south1.amazonaws.com/warning/863386074033605/2025-05-02/02_02_8_863386074033605_250502_155035.mp4?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-AmzDate=20250502T111326Z&X-Amz-SignedHeaders=host&X-Amz-Expires=604799&X-AmzCredential=AKIASZJLFSVFKHBGF7PE%2F20250502%2Fap-south-1%2Fs3%2Faws4_request&X-AmzSignature=0c4523d49a32193ebb5de229f1c68e7feab1c7bc012b69ccd7eaf390f21ff2ae"
                        ],
                        "message": "Alert VideoUrl fetched Successfully",
                        "status": 200
                      }`
          },
          {
            status: '400',
            description: 'Bad Request - Missing vehicleId',
            response: `{
                        "success": false,
                        "message": "Invalid request body",
                        "errors": [
                          {
                            "code": "missing_field",
                            "field": "vehicleId",
                            "detail": "VehicleId is required"
                          }
                        ]
                      }`
          },
          {
            status: '400',
            description: 'Bad Request - Missing timestamp',
            response: `{
                          "success": false,
                          "message": "Invalid request body",
                          "errors": [
                            {
                              "code": "missing_field",
                              "field": "timestamp",
                              "detail": "timestamp is required"
                            }
                          ]
                        }`
          },
          {
            status: '401',
            description: 'Unauthorized - Invalid credentials',
            response: `{
                          "success": false,
                          "message": "Invalid username or password"
                        }`
          }
        ]}
      />
    </Box>
    <Box id="sdCardVideos">
      <SdCardVehicleApis />
    </Box>
  </Box>
);

export default VehicleApis;
